import React, { useEffect, useState } from "react"
import { ShoppingCartIcon, PlayCircleIcon, TrashIcon } from "@heroicons/react/24/outline"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { twMerge } from "tailwind-merge"
import PriceDisplay from "./PriceDisplay"
import useT from "../hooks/useT"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { orderLimitOf, overOrderLimit, basePrice, isOutOfStock as computeOutOfStock, isSoldInCoins, isOwned, isPlayerContent, isRepeatPurchase, profileOf, stockSourceOf } from "../utils/productUtils"
import { openPlayer } from "../player"
import { useStoreAccount, ownsProduct } from "../store/account"
import { useCurrency } from "../store/currency"
import { addToCart, updateCartLine, removeFromCart, useCart, cartQuantityOf, cartLineOf } from "../store/cart"
import { hasPlatformSelection, lineKey } from "../checkout/cartItems"
import { openPlatformSelector } from "../checkout/PlatformSelectorDialog"
import { LoadProduct } from "../api"
import { useProductSelection } from "aldoo-ra/Store/extensions"
import { usePlacement } from "../store/placements"
import { useStoreExtensionText } from "aldoo-ra/Store/extensions/texts"
import useSiteLanguage from "../hooks/useSiteLanguage"

function DiscountChip({ percent }) {
  return <span className="inline-flex h-[18px] items-center bg-primary px-2 text-[12px] font-bold text-white">-{percent}%</span>
}

/**
 * Store product price + "Buy" (add to cart). Re-checks stock via product {load}
 * before adding; license products open the "Select your license" dialog
 * (quantity per platform) first. Tracking_AddToCart is published by the store.
 */
export default function ProductPurchaseUI({ product, quantity = 1, compact = false, hidePrice = false, hideButton = false, fullWidth = false, className = "", placement: ownPlacement, onAdded = null }) {
  // the product page says which of its buy buttons this is (store/placements)
  const placement = usePlacement(ownPlacement)
  const t = useT()
  const cart = useCart()
  const { currency, convert } = useCurrency()
  const discountPercent = activeDiscountPercent(product)
  const [stockOverride, setStockOverride] = useState(null)
  // Server-rendered button stays disabled until React is attached, so an
  // early click cannot be lost before hydration
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])
  const { account } = useStoreAccount()
  const license = hasPlatformSelection(product)
  // the product page's selection (store product extensions): null elsewhere,
  // and for a product with no extension it is always complete and unpriced
  const selection = useProductSelection(product)
  const incomplete = !!selection && !selection.isComplete
  // what is still to choose (a size, an outfit's part): Buy stays pressable
  // and says so; it is off only when the picks themselves cannot be bought
  const missing = incomplete ? selection.missing || [] : []
  const blocked = incomplete && !missing.length
  const [hint, setHint] = useState(false)
  const storeText = useStoreExtensionText(useSiteLanguage())
  useEffect(() => {
    if (!hint) return
    const timer = setTimeout(() => setHint(false), 4000)
    return () => clearTimeout(timer)
  }, [hint])
  useEffect(() => {
    if (!missing.length) setHint(false)
  }, [missing.length])
  // a product with variants or an outfit: the server's in_stock / stock are
  // derived from them; a chosen variant answers for itself, counted against
  // the cart's lines of that sku
  const sku = selection?.line?.sku || ""
  const inCart = sku ? (cart || []).reduce((n, line) => (line.pid === product.pid && line.sku === sku ? n + (line.quantity || 0) : n), 0) : cartQuantityOf(cart, product.pid)
  const stockSource = stockSourceOf(stockOverride ? { ...product, ...stockOverride } : product, selection?.line)
  const outOfStock = computeOutOfStock(stockSource, inCart)
  // how many one order may hold (the seller's rule): reached, Buy says so instead of adding
  const orderLimit = license ? null : orderLimitOf(product)
  const limitReached = orderLimit !== null && overOrderLimit(product, cartQuantityOf(cart, product.pid), quantity > 1 ? quantity : 1)
  const limitMessage = orderLimit ? (product.orderContact ? t("You can order up to %s of this item at once. For more, write to %s.", orderLimit, product.orderContact) : t("You can order up to %s of this item at once.", orderLimit)) : ""

  const checkAvailability = async () => {
    try {
      const result = await LoadProduct({ pid: product.pid, fields: "availableForPurchase stock in_stock" })
      if (result && result.availableForPurchase !== undefined) {
        setStockOverride({ availableForPurchase: result.availableForPurchase, stock: result.stock, in_stock: result.in_stock })
        return !computeOutOfStock(stockSourceOf({ ...product, ...result }, selection?.line), inCart)
      }
    } catch {}
    return !outOfStock
  }

  // Buy with choices missing: the selectors mark theirs, the first scrolls into
  // view, and a bubble over the button names them
  const askForChoices = () => {
    selection.requestCompletion?.()
    setHint(true)
    if (typeof document === "undefined") return
    requestAnimationFrame(() => document.querySelector("[data-missing]")?.scrollIntoView({ behavior: "smooth", block: "center" }))
  }

  // the product page's buy button goes on to the cart at once (most who only saw a toast never opened
  // it); the cart's own upsell panel (onAdded) is already in the cart
  const opensCart = !onAdded

  const handleBuy = async () => {
    if (outOfStock) return
    if (limitReached) return showErrorToast(limitMessage)
    if (missing.length) return askForChoices()
    if (incomplete) return
    if (!(await checkAvailability())) return
    if (license) {
      const existing = cartLineOf(cart, product.pid)
      return openPlatformSelector({
        product,
        initialDistribution: existing?.options?.platform,
        initialQuantity: existing?.quantity,
        confirmLabel: existing ? t`Update` : t`Add to Cart`,
        onConfirm: ({ platform, licenses, universal }) => {
          // adding says so itself (store/cart.js); changing a line does not
          if (!existing) return addToCart(product, licenses, { platform, licenses, universal }, { placement, proceedToCart: opensCart })
          updateCartLine(product.pid, licenses, { platform, licenses, universal }, placement)
          showSuccessToast(t`Cart updated`, product.name, { duration: 2000, enableDismissalTracking: false })
        },
      })
    }
    // the line the selection resolved to: its sku, choices and components go
    // with the cart line (the server prices them again)
    addToCart(product, quantity > 1 ? quantity : 1, {}, { selection: selection ? { ...selection.line, priced: selection.priced } : null, placement, proceedToCart: opensCart })
    // whoever opened this buy button for one add (the cart's upsell panel) closes it
    onAdded?.()
  }

  const soldInCoins = isSoldInCoins(product)

  const renderPrice = () => {
    if (product.free_access) return null
    const size = compact ? "medium" : "large"
    const qty = quantity > 0 ? quantity : 1
    if (soldInCoins) {
      const original = product.rent_cost ?? product.cost
      return (
        // the price paid, and beside it the struck original over the discount chip
        <div className={twMerge("flex flex-row items-center gap-2", fullWidth ? "justify-end" : "justify-start")}>
          <PriceDisplay amount={applyDiscount(original, discountPercent)} currency="coins" divisor={1} variant={size} className={discountPercent > 0 ? "text-primary" : ""} />
          {discountPercent > 0 && (
            <div className="flex flex-col items-start gap-1">
              <PriceDisplay amount={original} currency="coins" divisor={1} variant="small" struck className="opacity-60" />
              <DiscountChip percent={discountPercent} />
            </div>
          )}
        </div>
      )
    }
    // the product's price in the currency the visitor chose — or what an
    // extension made of it for the buyer's selection (a size, an outfit)
    const own = basePrice(product)
    const line = selection?.priced ? selection.line : null
    const base = line && Number.isFinite(line.amount) ? { amount: line.amount, currency: line.currency || own?.currency } : own
    if (!base) return null
    const parsed = { amount: convert(base.amount, base.currency), currency }
    const original = parsed.amount * qty
    return (
      // the price paid, and beside it the struck original over the discount chip
      <div className={twMerge("flex flex-row items-center gap-2", fullWidth ? "justify-end" : "justify-start")}>
        <PriceDisplay 
          amount={applyDiscount(original, discountPercent)} 
          currency={parsed.currency} 
          variant={size} 
          className={twMerge("min-w-0", discountPercent > 0 && "text-primary")} 
        />
        {discountPercent > 0 && (
          // top-aligned with the price's cents (their pt-[2px]), at their size; the
          // chip's bottom on the whole part's baseline (~0.16em above its box's bottom)
          <div className={twMerge("flex flex-col items-center justify-between self-stretch pt-[2px]", size === "medium" ? "pb-[7px]" : "pb-[8px]")}>
            <PriceDisplay
              amount={original}
              currency={parsed.currency}
              variant={`${size}_struck`}
              struck 
              className="opacity-60 min-w-0" 
            />
            <div className="flex items-center justify-center w-full h-[18px] bg-primary rounded-sm">
              <DiscountChip percent={discountPercent} />
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!product.cost && !product.real_money_cost && !product.real_price) return null
  // owned content (never a licence or a paper book) opens instead of selling
  // again: the product's own answer, or the store account's list (refreshed
  // the moment a purchase settles)
  const owned = !isRepeatPurchase(product) && (isOwned(product) || ownsProduct(account, product.pid))
  const showBuy = !hideButton && !product.free_access && !soldInCoins && !owned
  // had once (a download, access to content) and already in the cart: the
  // button takes it out — there is no second one to add
  const singleInCart = showBuy && !isRepeatPurchase(product) && !!profileOf(product) && inCart > 0
  const removeLine = () => {
    const line = cartLineOf(cart, product.pid)
    if (line) removeFromCart(lineKey(line))
  }
  const showOpen = !hideButton && owned
  // the player's content plays here; a download opens the account's digital
  // products on that one product (the page's search takes the pid from the url)
  const handleOpen = () => (isPlayerContent(product) ? openPlayer(product.pid) : navigate(`/user/digital-products?q=${encodeURIComponent(product.pid)}`))

  const price = !hidePrice && renderPrice()
  return (
    <div className={twMerge("flex items-center gap-4", fullWidth ? "flex-col items-stretch w-full" : "flex-row flex-nowrap justify-between", className)}>
      {/* in the row the price keeps its width; the button takes the rest
          and shrinks with the card (min-w-0 over the variant's 250px), up to 300px */}
      {price && <div className="shrink-0">{price}</div>}
      {showOpen && (
        <Button variant={compact ? "primary_dark" : "primary_dark_l"} className={twMerge(fullWidth && "w-full", compact && "min-w-[200px]", !compact && !fullWidth && "min-w-0 flex-1 max-w-[300px]")} onClick={handleOpen} disabled={!ready} data-testid="open-button">
          <PlayCircleIcon className="w-6 h-6" />
          {t`Open`}
        </Button>
      )}
      {showBuy && (
        <div className={twMerge("relative", fullWidth && "w-full", !compact && !fullWidth && "min-w-0 flex-1 max-w-[300px]")}>
        {hint && missing.length > 0 && (
          <div role="alert" data-testid="buy-missing" className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-20 w-max max-w-[280px] rounded-lg bg-text px-4 py-2 text-center text-sm font-semibold text-white shadow-lg">
            {storeText("choose")}: {missing.map((entry) => entry.label).join(", ")}
            <span aria-hidden className="absolute left-1/2 top-full -translate-x-1/2 border-8 border-transparent border-t-text" />
          </div>
        )}
        {singleInCart ? (
          <Button variant={compact ? "primary_dark" : "primary_dark_l"} className={twMerge("w-full", compact && "min-w-[200px]")} onClick={removeLine} data-testid="remove-from-cart-button">
            <TrashIcon className="w-6 h-6" />
            {t`Remove from cart`}
          </Button>
        ) : (
        <Button
          variant={compact ? "primary_dark" : "primary_dark_l"}
          className={twMerge("w-full", compact && "min-w-[200px]")}
          onClick={handleBuy}
          disabled={outOfStock || blocked || !ready}
          aria-label={outOfStock ? t`Out of Stock` : t`Buy`}
          data-testid="buy-button"
        >
          {!outOfStock && <ShoppingCartIcon className="w-6 h-6" />}
          {outOfStock ? t`Out of Stock` : t`Buy`}
        </Button>
        )}
        {/* a count limit means nothing for what is had once */}
        {orderLimit !== null && !outOfStock && isRepeatPurchase(product) && (
          <div className={`mt-2 text-[13px] text-center ${limitReached ? "text-error" : "text-text_secondary"}`} data-testid="order-limit-note">
            {limitReached ? limitMessage : t("Up to %s of this item", orderLimit)}
          </div>
        )}
        </div>
      )}
    </div>
  )
}
